import React from 'react';
import PositivityFeedbackCell from "../positivity/PositivityFeedbackCell";
import {API} from "aws-amplify";
import {toast} from "react-toastify";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import {Carousel} from 'react-responsive-carousel';
import Masonry from "react-masonry-css";
import useInterval from 'beautiful-react-hooks/useInterval';

import {getLocalDateStringFromUTC} from "@/common/LocalTimeStamp";
import {hideAuthorsFromFocusBoard} from "@/shared/access";
import _ from "lodash";
import {withRouter} from "react-router";
import useDocumentTitle from "../../common/UseDocumentTitle";

function PublicFeedbackIndex(props) {
    useDocumentTitle('Feedback board');
    const {token} = props.match.params;
    const LIST_TAB = "LIST";
    const FOCUS_TAB = "FOCUS";
    const FULL_SCREEN_CLASS = "fullScreen";
    const [feedback, setFeedback] = React.useState({});
    const [activeTab, setActiveTab] = React.useState("");
    const [lastFeedbackLoadedAt, setLastFeedbackLoadedAt] = React.useState(0);
    const [showCarousel, setShowCarousel] = React.useState(false);
    const engageLogoUrl = '/powerdms-engage-FKA-logo-white.svg';
    const [currentItem, setCurrentItem] = React.useState(1);
    const [refreshRate, setRefreshRate] = React.useState(3600000);
    const [currentSlide, setCurrentSlide] = React.useState(0);

    // Create refs for each transition item
    const nodeRefs = React.useRef({});
    const carouselRef = React.useRef(null);
    const leftArrowRef = React.useRef(null);
    const rightArrowRef = React.useRef(null);
    const announcementRef = React.useRef(null);
    const lastAnnouncementRef = React.useRef('');
    const announcementTimeoutRef = React.useRef(null);
    const shouldAnnounceSlideChange = React.useRef(false);

    React.useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow, noarchive, nosnippet';
        document.head.appendChild(meta);
        return () => {
            if (document.head.contains(meta)) {
                document.head.removeChild(meta);
            }
        };
    }, []);

    React.useEffect(() => {
        document.body.classList.remove(FULL_SCREEN_CLASS);
        if (activeTab === FOCUS_TAB) {
            document.body.classList.add(FULL_SCREEN_CLASS);
            setShowCarousel(true);
            // Focus on left arrow when entering Focus view
            setTimeout(() => {
                if (leftArrowRef.current) {
                    leftArrowRef.current.focus();
                }
            }, 100);
        }
    }, [activeTab])

    const styles = {
        container: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
        }
    };

    const backgroundColor1 = {
        backgroundColor: "#6550B9"
    }
    const backgroundColor2 = {
        background: "#0096B9"
    }
    const backgroundColor3 = {
        background: "#0D74CE"
    }

    const colors = [
        backgroundColor1,
        backgroundColor2,
        backgroundColor3
    ]

    const loadFeedback = (since) => {
        let params = '';

        if (since > 0) {
            params += '?since=' + since;
        }
        API.get('Unauthorized', '/u/feedback-public/' + token + params)
            .then(response => {
                    if (response.status !== "NO_NEW_DATA") {
                        setFeedback(Object.assign({}, response));
                        if(feedback.lastUpdated) {
                            const timestamp = new Date(feedback.lastUpdated).getTime();
                            setLastFeedbackLoadedAt(timestamp);
                        }
                        response.refreshRate ? setRefreshRate(response.refreshRate * 1000 * 3600) : setRefreshRate(3600000);
                        setActiveTab(response.viewType);
                    }
                },
                error => {
                    let errorMessage = "Could not load public feedbacks: ";
                    if (!error.response) {
                        errorMessage += "check your network connection. This message will disappear once the connection is restored.";
                    } else {
                        errorMessage += error.response?.data?.message;
                    }
                    toast(errorMessage, { position: "top-center", type: "error" });
                }
            );
    }

    useInterval(() => {
        loadFeedback(lastFeedbackLoadedAt);
    }, refreshRate);

    React.useEffect(() => {
        loadFeedback(lastFeedbackLoadedAt);
    }, [])

    const handleSlideChange = (index) => {
        setCurrentItem(index + 1);
        shouldAnnounceSlideChange.current = true;
        setCurrentSlide(index);
    };

    // Announce slide content for screen readers
    const announceSlideContent = (index) => {
        if (!feedback.items || feedback.items.length === 0) return;

        const f = feedback.items[index];
        if (!f) return;

        let personnelInfo = '';
        if (f.personnel && f.feedbackSubject && !hideAuthorsFromFocusBoard()) {
            let names = [];
            if (f.feedbackSubject === "CALLTAKER") {
                names = f.personnel.filter(ep => ep.type === "CALLTAKER").map(ct => ct.name);
            } else if (f.feedbackSubject === "RESPONDER") {
                names = f.personnel.filter(ep => ep.type === "RESPONDER").map(resp => resp.name);
            } else if (f.feedbackSubject === "ALL") {
                names = f.personnel.map(p => p.name);
            }
            if (names.length > 0) {
                personnelInfo = `, Personnel: ${names.join(', ')}`;
            }
        }

        const dateInfo = f.date ? getLocalDateStringFromUTC(f.date, "MM/DD/YYYY, h:mm:ss a") : '';
        const announcement = `Item ${index + 1} of ${feedback.items.length}. Date: ${dateInfo}. Feedback: ${f.feedback}${personnelInfo}`;

        // Prevent duplicate announcements
        if (lastAnnouncementRef.current === announcement) {
            return;
        }

        // Clear any pending announcement
        if (announcementTimeoutRef.current) {
            clearTimeout(announcementTimeoutRef.current);
        }

        if (announcementRef.current) {
            // Clear first
            announcementRef.current.textContent = '';
            // Then announce after a brief delay
            announcementTimeoutRef.current = setTimeout(() => {
                if (announcementRef.current) {
                    announcementRef.current.textContent = announcement;
                    lastAnnouncementRef.current = announcement;
                }
            }, 100);
        }
    };

    // Track slide changes for announcements
    React.useEffect(() => {
        // Only announce if this is from carousel auto-scroll or arrow click
        if (activeTab === FOCUS_TAB &&
            feedback.items &&
            feedback.items.length > 0 &&
            shouldAnnounceSlideChange.current) {
            // This is a carousel slide change from auto-scroll or arrow click
            setTimeout(() => {
                announceSlideContent(currentSlide);
            }, 200);
        }
    }, [currentSlide]);

    const renderCustomPrevArrow = (onClickHandler, hasPrev, label) =>
        hasPrev && (
            <button type="button"
                    ref={leftArrowRef}
                    className="control-arrow control-prev"
                    tabIndex="0"
                    onClick={onClickHandler}
                    onFocus={() => {
                        // Disable auto-announcements temporarily
                        shouldAnnounceSlideChange.current = false;
                        // Announce current item when arrow receives focus
                        setTimeout(() => {
                            if (feedback.items && feedback.items.length > 0) {
                                announceSlideContent(currentSlide);
                            }
                        }, 100);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClickHandler();
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab to right arrow
                            e.preventDefault();
                            if (rightArrowRef.current) {
                                rightArrowRef.current.focus();
                            }
                        } else if (e.key === 'Tab' && e.shiftKey) {
                            // Shift+Tab wraps to right arrow
                            e.preventDefault();
                            if (rightArrowRef.current) {
                                rightArrowRef.current.focus();
                            }
                        }
                    }}
                    title={label}
                    aria-label={label}>
            </button>
        );

    const renderCustomNextArrow = (onClickHandler, hasNext, label) =>
        hasNext && (
            <button type="button"
                    ref={rightArrowRef}
                    className="control-arrow control-next"
                    tabIndex="0"
                    onClick={onClickHandler}
                    onFocus={() => {
                        // Disable auto-announcements temporarily
                        shouldAnnounceSlideChange.current = false;
                        // Announce current item when arrow receives focus
                        setTimeout(() => {
                            if (feedback.items && feedback.items.length > 0) {
                                announceSlideContent(currentSlide);
                            }
                        }, 100);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClickHandler();
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                            // Tab wraps to left arrow
                            e.preventDefault();
                            if (leftArrowRef.current) {
                                leftArrowRef.current.focus();
                            }
                        } else if (e.key === 'Tab' && e.shiftKey) {
                            // Shift+Tab to left arrow
                            e.preventDefault();
                            if (leftArrowRef.current) {
                                leftArrowRef.current.focus();
                            }
                        }
                    }}
                    title={label}
                    aria-label={label}>
            </button>
        );
    return (
        <div className={`rootView ${activeTab === 'FOCUS' ? "publicFocusedFeedbackView" : "publicFeedbackView"} positivityView`}>
            {/* Screen reader announcement region */}
            <div
                ref={announcementRef}
                role="status"
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
                style={{
                    position: 'absolute',
                    left: '-10000px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden'
                }}
            />

            <div className="navBar">
                <div
                    style={{
                        backgroundImage: `url("${engageLogoUrl}")`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        width: '135px',
                        height: '35px'
                    }}
                    className="logo">
                </div>
                <h1 className="title" id="feedback-board-title" tabIndex="-1">Feedback Board</h1>
            </div>
            {
                activeTab === LIST_TAB ? (
                    <TransitionGroup className="feedbackList tableView">
                        <Masonry className={"feedback-masonry"} breakpointCols={2}
                                 columnClassName={"feedback-masonry-column"}>
                            {
                                _.map(feedback.items, function (f, i) {
                                    let color = colors[(i % 3)];
                                    const itemKey = `${f.id}-${i}`;

                                    // Create or retrieve ref for this item
                                    if (!nodeRefs.current[itemKey]) {
                                        nodeRefs.current[itemKey] = React.createRef();
                                    }
                                    const nodeRef = nodeRefs.current[itemKey];

                                    return (
                                        <CSSTransition
                                            key={itemKey}
                                            nodeRef={nodeRef}
                                            timeout={5000}
                                            classNames="feedback-item"
                                        >
                                            <div ref={nodeRef}>
                                                <PositivityFeedbackCell
                                                    extraClasses={""}
                                                    feedbackStyle={color}
                                                    feedbackText={f.feedback}
                                                    surveyId={f.surveyId}
                                                    feedbackSubject={f.feedbackSubject}
                                                    encounterPersonnel={f.encounterPersonnel}
                                                />
                                            </div>
                                        </CSSTransition>
                                    )
                                })
                            }
                        </Masonry>
                    </TransitionGroup>
                ) : (
                    <div className="focusView">
                        <div className={"feedbackList tableView"}
                             role="region"
                             aria-label="Feedback Board"
                             aria-labelledby="feedback-board-title">
                            {(showCarousel &&
                                <Carousel
                                    ref={carouselRef}
                                    renderArrowPrev={(onClickHandler, hasPrev) =>
                                        renderCustomPrevArrow(onClickHandler, hasPrev, 'Previous feedback item')
                                    }
                                    renderArrowNext={(onClickHandler, hasNext) =>
                                        renderCustomNextArrow(onClickHandler, hasNext, 'Next feedback item')
                                    }
                                    centerMode={false}
                                    showArrows={true}
                                    showStatus={false}
                                    showIndicators={false}
                                    infiniteLoop={true}
                                    showThumbs={false}
                                    autoPlay={true}
                                    interval={10000}
                                    transitionTime={600}
                                    stopOnHover={false}
                                    onChange={handleSlideChange}
                                >
                                    {
                                        _.map(feedback.items, function (f, i) {
                                            let color = colors[(i % 3)];
                                            let responders, all, callTakers, responderNames, callTakerNames,
                                                allNames = new Set();

                                            if (f.personnel != null) {
                                                responders = new Set(f.personnel.filter(ep => ep.type === "RESPONDER").map(resp => resp.name));
                                                callTakers = new Set(f.personnel.filter(ep => ep.type === "CALLTAKER").map(ct => ct.name));
                                                all = new Set(f.personnel.map(p => p.name));
                                                responderNames = Array.from(responders);
                                                callTakerNames = Array.from(callTakers);
                                                allNames = Array.from(all);
                                            }

                                            // Build accessible label
                                            let accessibleLabel = `Feedback item ${i + 1} of ${feedback.items.length}`;
                                            const dateLabel = f.date ? getLocalDateStringFromUTC(f.date, "MM/DD/YYYY, h:mm:ss a") : '';

                                            return (
                                                <div style={Object.assign(color)}
                                                     className="feedbackCell focusedFeedback"
                                                     key={f.surveyAnswerId}
                                                     tabIndex="-1"
                                                     role="article"
                                                     aria-label={accessibleLabel}>
                                                    {f.feedback != null && f.feedback.length >= 400 ?
                                                        <div className="feedbackText small" aria-label={`Feedback: ${f.feedback}`}>{f.feedback}</div> :
                                                        f.feedback != null && f.feedback.length >= 200 && f.feedback.length < 400 ?
                                                            <div className="feedbackText medium" aria-label={`Feedback: ${f.feedback}`}>{f.feedback}</div> :
                                                            <div className="feedbackText big" aria-label={`Feedback: ${f.feedback}`}>{f.feedback}</div>
                                                    }
                                                    <div style={styles.container}>
                                                        <span className="feedbackDate" aria-label={`Date: ${dateLabel}`}>
                                                            {dateLabel}
                                                        </span>
                                                        {
                                                            f.personnel && f.feedbackSubject && f.feedbackSubject === "CALLTAKER" && !hideAuthorsFromFocusBoard() ? (
                                                                <p className="feedbackAuthor"
                                                                   style={{fontSize: '20px'}}
                                                                   aria-label={`Personnel: ${callTakerNames.join(', ')}`}>
                                                                    {callTakerNames.join(' - ')}
                                                                </p>
                                                            ) : null
                                                        }
                                                        {
                                                            f.personnel && f.feedbackSubject && f.feedbackSubject === "RESPONDER" && !hideAuthorsFromFocusBoard() ? (
                                                                <p className="feedbackAuthor"
                                                                   style={{fontSize: '20px'}}
                                                                   aria-label={`Personnel: ${responderNames.join(', ')}`}>
                                                                    {responderNames.join(' - ')}
                                                                </p>
                                                            ) : null
                                                        }
                                                        {
                                                            f.personnel && f.feedbackSubject && f.feedbackSubject === "ALL" && !hideAuthorsFromFocusBoard() ? (
                                                                <p className="feedbackAuthor"
                                                                   style={{fontSize: '20px'}}
                                                                   aria-label={`Personnel: ${allNames.join(', ')}`}>
                                                                    {allNames.join(' - ')}
                                                                </p>
                                                            ) : null
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        })
                                    }
                                </Carousel>)}
                        </div>
                    </div>
                )
            }
        </div>
    );
}

export default withRouter(PublicFeedbackIndex);